/**
 * TechnologyDetectionService.js
 * Service for detecting technologies used on websites
 */

/**
 * Service for detecting technologies used by a website
 */
class TechnologyDetectionService {
  /**
   * Initialize the technology detection service
   */
  constructor() {
    // Get API URL from environment or fallback to default
    const apiPort = process.env.REACT_APP_API_PORT || '5000';
    this.apiBaseUrl = process.env.REACT_APP_API_URL || `http://localhost:${apiPort}/api`;
    
    // CORS proxies to try in sequence
    this.corsProxies = [
      `${this.apiBaseUrl}/pagespeed/cors-proxy?url=`,
      'https://corsproxy.io/?',
      'https://cors-anywhere.herokuapp.com/'
    ];
    
    console.log('Initializing TechnologyDetectionService with API base URL:', this.apiBaseUrl);
    this.reset();
  }
  
  /**
   * Reset detection state
   */
  reset() {
    this.results = [];
    this.completed = false;
    this.isLoading = false;
    this.error = null;
  }
  
  /**
   * Detect technologies used by a website
   * Primary method for technology detection
   * @param {string} url - URL to analyze
   * @returns {Promise<object>} Detection result with technologies
   */
  async detectTechnologies(url) {
    this.reset();
    
    if (!url) {
      console.error('URL is required for technology detection');
      return {
        error: 'URL is required',
        technologies: []
      };
    }
    
    try {
      console.log(`[Technology Detection] Analyzing ${url}`);
      
      // First, try server-side comprehensive technology detection
      console.log('[Technology Detection] Using server-side detection');
      const serverResult = await this._useServerSideTechnologyDetection(url);
      
      // Start with the client-side analysis regardless of server result
      console.log('[Technology Detection] Running client-side detection methods');
      
      // Start with basic domain analysis
      let clientTechnologies = [];
      
      // Add technologies from domain analysis
      const domainTechnologies = await this._analyzeDomain(url);
      clientTechnologies = [...clientTechnologies, ...domainTechnologies];
      
      // Try to get domain info from server
      const domainInfoTechnologies = await this._fetchDomainInfo(url);
      if (domainInfoTechnologies && Array.isArray(domainInfoTechnologies) && domainInfoTechnologies.length > 0) {
        clientTechnologies = [...clientTechnologies, ...domainInfoTechnologies];
      }
      
      // Try specific technology endpoint
      const techData = await this._fetchTechnologyData(url);
      if (techData && Array.isArray(techData) && techData.length > 0) {
        clientTechnologies = [...clientTechnologies, ...techData];
      }
      
      // If we still don't have many technologies, add some basic ones
      if (clientTechnologies.length < 3) {
        const basicTechs = this._getBasicTechnologies(url);
        clientTechnologies = [...clientTechnologies, ...basicTechs];
      }
      
      // Now merge server and client results
      let allTechnologies = [];
      
      // Add server results if they exist
      if (serverResult && Array.isArray(serverResult) && serverResult.length > 0) {
        console.log(`[Technology Detection] Server detected ${serverResult.length} technologies`);
        // Mark server technologies with source if not already present
        const serverTechnologies = serverResult.map(tech => ({
          ...tech,
          source: tech.source || 'server-side'
        }));
        allTechnologies = [...allTechnologies, ...serverTechnologies];
      }
      
      // Add client technologies
      if (clientTechnologies.length > 0) {
        console.log(`[Technology Detection] Client detected ${clientTechnologies.length} technologies`);
        // Mark client technologies with source if not already present
        const markedClientTechs = clientTechnologies.map(tech => ({
          ...tech,
          source: tech.source || 'client-side'
        }));
        allTechnologies = [...allTechnologies, ...markedClientTechs];
      }
      
      // Deduplicate technologies
      const uniqueTechnologies = this._deduplicateTechnologies(allTechnologies);
      console.log('[Technology Detection] Final merged technologies:', uniqueTechnologies);
      
      // Set the detection state and return result
      this.detectionState = {
        url,
        detected: true,
        technologies: uniqueTechnologies,
        timestamp: new Date().toISOString(),
        method: 'combined',
        serverDetectionUsed: serverResult && Array.isArray(serverResult) && serverResult.length > 0,
        clientDetectionUsed: clientTechnologies.length > 0
      };
      
      return this.detectionState;
    } catch (error) {
      console.error('[Technology Detection] Error:', error);
      
      // Handle the error gracefully by returning basic technologies
      const basicTechnologies = this._getBasicTechnologies(url);
      
      this.detectionState = {
        url,
        detected: true,
        technologies: basicTechnologies,
        timestamp: new Date().toISOString(),
        warning: 'Error in detection, showing basic technologies',
        method: 'fallback'
      };
      
      return this.detectionState;
    }
  }
  
  /**
   * Analyze domain to extract basic technology information
   * @private
   * @param {string} url - URL to analyze
   * @returns {Promise<Array>} - Technologies detected from domain
   */
  async _analyzeDomain(url) {
    try {
      const domain = new URL(url).hostname;
      const technologies = [];
      
      // Check for known hosting platforms in domain
      if (domain.includes('github.io')) {
        technologies.push({
          name: 'GitHub Pages',
          type: 'hosting',
          confidence: 'high',
          source: 'domain-analysis'
        });
      } else if (domain.includes('netlify.app')) {
        technologies.push({
          name: 'Netlify',
          type: 'hosting',
          confidence: 'high',
          source: 'domain-analysis'
        });
      } else if (domain.includes('vercel.app')) {
        technologies.push({
          name: 'Vercel',
          type: 'hosting',
          confidence: 'high',
          source: 'domain-analysis'
        });
      } else if (domain.includes('herokuapp.com')) {
        technologies.push({
          name: 'Heroku',
          type: 'hosting',
          confidence: 'high',
          source: 'domain-analysis'
        });
      } else if (domain.includes('wpengine.com')) {
        technologies.push({
          name: 'WP Engine',
          type: 'hosting',
          confidence: 'high',
          source: 'domain-analysis'
        });
        technologies.push({
          name: 'WordPress',
          type: 'cms',
          confidence: 'high',
          source: 'domain-analysis'
        });
      } else if (domain.includes('squarespace.com')) {
        technologies.push({
          name: 'Squarespace',
          type: 'cms',
          confidence: 'high',
          source: 'domain-analysis'
        });
      } else if (domain.includes('wix.com')) {
        technologies.push({
          name: 'Wix',
          type: 'cms',
          confidence: 'high',
          source: 'domain-analysis'
        });
      } else if (domain.includes('shopify.com')) {
        technologies.push({
          name: 'Shopify',
          type: 'cms',
          confidence: 'high',
          source: 'domain-analysis'
        });
      }
      
      // Protocol detection
      if (url.startsWith('https://')) {
        technologies.push({
          name: 'HTTPS',
          type: 'protocol',
          confidence: 'high',
          source: 'domain-analysis'
        });
        technologies.push({
          name: 'SSL/TLS',
          type: 'security',
          confidence: 'high',
          source: 'domain-analysis'
        });
      } else if (url.startsWith('http://')) {
        technologies.push({
          name: 'HTTP',
          type: 'protocol',
          confidence: 'medium',
          source: 'domain-analysis'
        });
      }
      
      return technologies;
    } catch (error) {
      console.warn('[Technology Detection] Domain analysis error:', error.message);
      return [];
    }
  }
  
  /**
   * Get basic technologies based on URL patterns
   * Used as a fallback when server-side detection fails
   * @private
   * @param {string} url - URL to analyze
   * @returns {Array} - Basic detected technologies
   */
  _getBasicTechnologies(url) {
    const technologies = [];
    let urlObj;
    
    try {
      urlObj = new URL(url);
    } catch (error) {
      console.warn('[Technology Detection] Invalid URL:', error.message);
      return [
        { name: 'HTML', type: 'frontend', confidence: 'high', source: 'basic-fallback' },
        { name: 'CSS', type: 'frontend', confidence: 'high', source: 'basic-fallback' },
        { name: 'JavaScript', type: 'frontend', confidence: 'high', source: 'basic-fallback' }
      ];
    }
    
    // Add basic web technologies that are almost always present
    technologies.push({
      name: 'HTML',
      type: 'frontend',
      confidence: 'high',
      source: 'basic-analysis'
    });
    
    technologies.push({
      name: 'CSS',
      type: 'frontend',
      confidence: 'high',
      source: 'basic-analysis'
    });
    
    technologies.push({
      name: 'JavaScript',
      type: 'frontend',
      confidence: 'high',
      source: 'basic-analysis'
    });
    
    // Check protocol (HTTP vs HTTPS)
    if (url.startsWith('https://')) {
      technologies.push({
        name: 'HTTPS',
        type: 'protocol',
        confidence: 'high',
        source: 'basic-analysis'
      });
      
      technologies.push({
        name: 'SSL/TLS',
        type: 'security',
        confidence: 'high',
        source: 'basic-analysis'
      });
    } else if (url.startsWith('http://')) {
      technologies.push({
        name: 'HTTP',
        type: 'protocol',
        confidence: 'high',
        source: 'basic-analysis'
      });
    }
    
    // Check for common file extensions in URL that might indicate technology
    const path = urlObj.pathname.toLowerCase();
    
    // PHP detection
    if (path.includes('.php')) {
      technologies.push({
        name: 'PHP',
        type: 'backend',
        confidence: 'high',
        source: 'url-analysis'
      });
    }
    
    // ASP.NET detection
    if (path.includes('.aspx') || path.includes('.asp')) {
      technologies.push({
        name: 'ASP.NET',
        type: 'backend',
        confidence: 'high',
        source: 'url-analysis'
      });
    }
    
    // JSP detection
    if (path.includes('.jsp')) {
      technologies.push({
        name: 'Java',
        type: 'backend',
        confidence: 'high',
        source: 'url-analysis'
      });
    }
    
    // Ruby detection
    if (path.includes('.rb')) {
      technologies.push({
        name: 'Ruby',
        type: 'backend',
        confidence: 'high',
        source: 'url-analysis'
      });
    }
    
    // CMS path detection
    if (path.includes('/wp-content/') || path.includes('/wp-includes/') || path.includes('/wp-admin/')) {
      technologies.push({
        name: 'WordPress',
        type: 'cms',
        confidence: 'high',
        source: 'url-analysis'
      });
    } else if (path.includes('/sites/default/files/') || path.includes('/modules/')) {
      technologies.push({
        name: 'Drupal',
        type: 'cms',
        confidence: 'medium',
        source: 'url-analysis'
      });
    } else if (path.includes('/components/com_') || path.includes('/administrator/')) {
      technologies.push({
        name: 'Joomla',
        type: 'cms',
        confidence: 'medium',
        source: 'url-analysis'
      });
    }
    
    // Domain-based detection (if not already covered by _analyzeDomain)
    const domain = urlObj.hostname.toLowerCase();
    
    if (domain.includes('webflow.io')) {
      technologies.push({
        name: 'Webflow',
        type: 'cms',
        confidence: 'high',
        source: 'url-analysis'
      });
    } else if (domain.includes('myshopify.com')) {
      technologies.push({
        name: 'Shopify',
        type: 'cms',
        confidence: 'high',
        source: 'url-analysis'
      });
    } else if (domain.includes('squarespace.com')) {
      technologies.push({
        name: 'Squarespace',
        type: 'cms',
        confidence: 'high',
        source: 'url-analysis'
      });
    } else if (domain.includes('wixsite.com')) {
      technologies.push({
        name: 'Wix',
        type: 'cms',
        confidence: 'high',
        source: 'url-analysis'
      });
    }
    
    // Every site has a web server
    technologies.push({
      name: 'Web Server',
      type: 'server',
      confidence: 'low',
      source: 'basic-analysis'
    });
    
    return technologies;
  }
  
  /**
   * Perform domain analysis to extract basic information
   * @private
   * @param {string} url - URL to analyze
   * @returns {Promise<Object>} - Domain information
   */
  async _performDomainAnalysis(url) {
    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname;
      
      console.log(`Analyzing domain: ${domain}`);
      
      // Basic domain info structure
      const domainInfo = {
        domain: domain,
        tld: domain.split('.').pop(),
        registrar: null,
        hosting: null,
        nameservers: []
      };
      
      // Check for common hosting providers based on CNAME or nameserver patterns
      const commonHostingPatterns = {
        'Cloudflare': ['cloudflare.com', 'cloudflare.net'],
        'AWS': ['amazonaws.com', 'aws.amazon.com'],
        'DigitalOcean': ['digitalocean.com'],
        'Heroku': ['herokuapp.com', 'heroku.com'],
        'Netlify': ['netlify.app', 'netlify.com'],
        'Vercel': ['vercel.app', 'vercel.com'],
        'GitHub Pages': ['github.io', 'githubusercontent.com'],
        'WP Engine': ['wpengine.com'],
        'Squarespace': ['squarespace.com'],
        'Wix': ['wix.com', 'wixsite.com'],
        'Shopify': ['shopify.com', 'myshopify.com'],
        'Webflow': ['webflow.io', 'webflow.com'],
        'GoDaddy': ['godaddy.com', 'secureserver.net'],
        'HostGator': ['hostgator.com'],
        'Bluehost': ['bluehost.com'],
        'InMotion': ['inmotionhosting.com'],
        'DreamHost': ['dreamhost.com'],
        'SiteGround': ['siteground.com']
      };
      
      // Check if domain contains any common hosting provider patterns
      for (const [provider, patterns] of Object.entries(commonHostingPatterns)) {
        if (patterns.some(pattern => domain.includes(pattern))) {
          domainInfo.hosting = provider;
          break;
        }
      }
      
      // Try to get domain info from our server API first
      try {
        const serverDomainInfo = await this._fetchServerDomainInfo(url);
        if (serverDomainInfo) {
          Object.assign(domainInfo, serverDomainInfo);
        }
      } catch (error) {
        console.warn('Failed to fetch domain info from server:', error.message);
      }
      
      return domainInfo;
    } catch (error) {
      console.error('Domain analysis error:', error);
      return null;
    }
  }
  
  /**
   * Extract technologies from domain information
   * @private
   * @param {string} url - URL being analyzed
   * @param {Object} domainInfo - Domain information
   * @param {Array} results - Array to store detected technologies
   */
  _detectFromDomainInfo(url, domainInfo, results) {
    if (!domainInfo) return;
    
    try {
      // Add hosting provider as technology if detected
      if (domainInfo.hosting) {
        results.push({
          name: domainInfo.hosting,
          type: 'hosting',
          confidence: 'high',
          source: 'domain-analysis'
        });
      }
      
      // Add registrar as technology if detected
      if (domainInfo.registrar) {
        results.push({
          name: domainInfo.registrar,
          type: 'registrar',
          confidence: 'high',
          source: 'domain-analysis'
        });
      }
      
      // Check for specific TLDs that indicate certain technologies
      if (domainInfo.tld) {
        const tldTechs = {
          'shopify.com': { name: 'Shopify', type: 'ecommerce' },
          'myshopify.com': { name: 'Shopify', type: 'ecommerce' },
          'wix.com': { name: 'Wix', type: 'cms' },
          'wixsite.com': { name: 'Wix', type: 'cms' },
          'squarespace.com': { name: 'Squarespace', type: 'cms' },
          'webflow.io': { name: 'Webflow', type: 'cms' },
          'herokuapp.com': { name: 'Heroku', type: 'hosting' },
          'netlify.app': { name: 'Netlify', type: 'hosting' },
          'vercel.app': { name: 'Vercel', type: 'hosting' },
          'github.io': { name: 'GitHub Pages', type: 'hosting' },
          'wpengine.com': { name: 'WP Engine', type: 'hosting' }
        };
        
        const domain = domainInfo.domain;
        
        for (const [pattern, tech] of Object.entries(tldTechs)) {
          if (domain.endsWith(pattern)) {
            results.push({
              name: tech.name,
              type: tech.type,
              confidence: 'high',
              source: 'domain-analysis'
            });
          }
        }
      }
      
      // Check for nameservers that indicate certain technologies
      if (domainInfo.nameservers && domainInfo.nameservers.length > 0) {
        const nsPatterns = {
          'cloudflare.com': { name: 'Cloudflare', type: 'cdn' },
          'shopify.com': { name: 'Shopify', type: 'ecommerce' },
          'wix.com': { name: 'Wix', type: 'cms' },
          'squarespace.com': { name: 'Squarespace', type: 'cms' },
          'dreamhost.com': { name: 'DreamHost', type: 'hosting' },
          'namecheap.com': { name: 'Namecheap', type: 'registrar' },
          'godaddy.com': { name: 'GoDaddy', type: 'registrar' }
        };
        
        for (const ns of domainInfo.nameservers) {
          for (const [pattern, tech] of Object.entries(nsPatterns)) {
            if (ns.includes(pattern)) {
              results.push({
                name: tech.name,
                type: tech.type,
                confidence: 'high',
                source: 'nameserver-analysis'
              });
              break;
            }
          }
        }
      }
    } catch (error) {
      console.error('Error detecting from domain info:', error);
    }
  }
  
  /**
   * Perform deep HTML analysis to extract more technologies
   * @private
   * @param {string} html - HTML content
   * @param {Array} results - Array to store detected technologies
   */
  _detectFromDeepHTMLAnalysis(html, results) {
    if (!html) return;
    
    try {
      // Create a document parser
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Check for common CMS patterns in HTML
      // WordPress
      if (html.includes('wp-content') || 
          html.includes('wp-includes') || 
          doc.querySelector('[class*="wp-"]')) {
        results.push({
          name: 'WordPress',
          type: 'cms',
          confidence: 'high',
          source: 'deep-html-analysis'
        });
      }
      
      // Drupal
      if (html.includes('Drupal.settings') || 
          html.includes('/sites/default/files/') || 
          doc.querySelector('[class*="drupal-"]')) {
        results.push({
          name: 'Drupal',
          type: 'cms',
          confidence: 'high',
          source: 'deep-html-analysis'
        });
      }
      
      // Joomla
      if (html.includes('/media/jui/') || 
          html.includes('/media/system/') || 
          html.includes('joomla')) {
        results.push({
          name: 'Joomla',
          type: 'cms',
          confidence: 'high',
          source: 'deep-html-analysis'
        });
      }
      
      // Modern frontend frameworks
      // React
      if (html.includes('_reactRootContainer') || 
          html.includes('__REACT_ROOT__') || 
          doc.querySelector('[data-reactroot]') ||
          doc.querySelector('[data-reactid]')) {
        results.push({
          name: 'React',
          type: 'frontend',
          confidence: 'high',
          source: 'deep-html-analysis'
        });
      }
      
      // Vue.js
      if (html.includes('__vue__') || 
          html.includes('vue.js') || 
          html.includes('vue.min.js') ||
          doc.querySelector('[data-v-') ||
          doc.querySelector('[v-cloak]')) {
        results.push({
          name: 'Vue.js',
          type: 'frontend',
          confidence: 'high',
          source: 'deep-html-analysis'
        });
      }
      
      // Angular
      if (html.includes('ng-app') || 
          html.includes('angular.js') || 
          html.includes('angular.min.js') ||
          doc.querySelector('[ng-')) {
        results.push({
          name: 'Angular',
          type: 'frontend',
          confidence: 'high',
          source: 'deep-html-analysis'
        });
      }
      
      // Common ecommerce platforms
      // Shopify
      if (html.includes('shopify') || 
          html.includes('Shopify.theme') || 
          html.includes('/cdn.shopify.com/')) {
        results.push({
          name: 'Shopify',
          type: 'ecommerce',
          confidence: 'high',
          source: 'deep-html-analysis'
        });
      }
      
      // WooCommerce
      if (html.includes('woocommerce') || 
          html.includes('WooCommerce') || 
          doc.querySelector('[class*="woocommerce"]')) {
        results.push({
          name: 'WooCommerce',
          type: 'ecommerce',
          confidence: 'high',
          source: 'deep-html-analysis'
        });
      }
      
      // Magento
      if (html.includes('Mage.') || 
          html.includes('magento') || 
          html.includes('Magento')) {
        results.push({
          name: 'Magento',
          type: 'ecommerce',
          confidence: 'high',
          source: 'deep-html-analysis'
        });
      }
      
      // Hosted services
      // Netlify
      if (html.includes('netlify') || 
          doc.querySelector('meta[name="netlify"]')) {
        results.push({
          name: 'Netlify',
          type: 'hosting',
          confidence: 'high',
          source: 'deep-html-analysis'
        });
      }
      
      // Vercel
      if (html.includes('vercel') || 
          html.includes('/_next/')) {
        results.push({
          name: 'Vercel',
          type: 'hosting',
          confidence: 'high',
          source: 'deep-html-analysis'
        });
      }
      
      // Domain Registrar Detection
      // Check for common DNS providers and registrars in meta tags, scripts, or comments
      const registrarPatterns = {
        'GoDaddy': ['godaddy', 'parked-content.godaddy.com', 'secureserver.net'],
        'Namecheap': ['namecheap', 'parkingpage.namecheap.com'],
        'Google Domains': ['google domains', 'domains.google.com', 'googledomains'],
        'Cloudflare Registrar': ['cloudflare', 'registrar.cloudflare.com'],
        'Domain.com': ['domain.com'],
        'Hover': ['hover.com'],
        'Amazon Route 53': ['route53', 'aws-dns'],
        'NameSilo': ['namesilo'],
        'Network Solutions': ['networksolutions'],
        'Bluehost': ['bluehost.com'],
        'DreamHost': ['dreamhost.com'],
        'OVH': ['ovh.com', 'ovhcloud'],
        'Squarespace Domains': ['domains.squarespace.com'],
        'Wix Domain Services': ['wixdns.net', 'wixregistrar'],
        'HostGator': ['hostgator'],
        'Name.com': ['name.com'],
        'Dynadot': ['dynadot'],
        'Porkbun': ['porkbun'],
        'Epik': ['epik.com']
      };
      
      for (const [registrar, patterns] of Object.entries(registrarPatterns)) {
        if (patterns.some(pattern => html.toLowerCase().includes(pattern))) {
          results.push({
            name: registrar,
            type: 'domain-registrar',
            confidence: 'medium',
            source: 'deep-html-analysis'
          });
          break; // Only add the most likely registrar
        }
      }
      
      // Hosting Provider Detection
      // Look for specific hosting provider signatures in the HTML
      const hostingPatterns = {
        'WP Engine': ['wpengine', 'wpe-'],
        'Kinsta': ['kinsta', 'kinsta-cdn'],
        'Bluehost': ['bluehost', 'bluehost.com'],
        'SiteGround': ['siteground', 'sgvps.net'],
        'HostGator': ['hostgator', 'hostgator.com'],
        'A2 Hosting': ['a2hosting'],
        'DreamHost': ['dreamhost', 'dreamhosters'],
        'InMotion Hosting': ['inmotionhosting'],
        'DigitalOcean': ['digitalocean', 'nyc3.digitaloceanspaces.com'],
        'Linode': ['linode.com', 'linode'],
        'AWS': ['amazonaws.com', 'aws.amazon.com', 's3.amazonaws'],
        'GoDaddy Hosting': ['godaddy.com/hosting', 'wsc.godaddy'],
        'Google Cloud': ['googleusercontent.com', 'storage.googleapis.com'],
        'Microsoft Azure': ['azurewebsites.net', 'azure-api'],
        'OVH Hosting': ['ovh.net', 'ovhcloud'],
        'Hostinger': ['hostinger'],
        '1&1 IONOS': ['ionos', '1and1'],
        'GreenGeeks': ['greengeeks'],
        'Pantheon': ['pantheonsite.io', 'pantheon'],
        'Flywheel': ['getflywheel', 'flywheelsites'],
        'Cloudways': ['cloudways'],
        'Rackspace': ['rackspace', 'rackcdn.com']
      };
      
      for (const [hosting, patterns] of Object.entries(hostingPatterns)) {
        if (patterns.some(pattern => html.toLowerCase().includes(pattern))) {
          results.push({
            name: hosting,
            type: 'hosting',
            confidence: 'medium',
            source: 'deep-html-analysis'
          });
          break; // Only add the most likely hosting provider
        }
      }
      
      // CDN Detection
      const cdnPatterns = {
        'Cloudflare': ['cloudflare', 'cloudflare-static', '__cf_'],
        'Cloudflare CDN': ['cdnjs.cloudflare.com'],
        'AWS CloudFront': ['cloudfront.net'],
        'Akamai': ['akamai', 'akamaiedge.net', 'akamaihd.net'],
        'Fastly': ['fastly', 'fastly.net'],
        'MaxCDN': ['maxcdn'],
        'KeyCDN': ['keycdn'],
        'Bunny CDN': ['bunnycdn'],
        'StackPath': ['stackpathdns'],
        'Google Cloud CDN': ['google', 'googleusercontent.com'],
        'Microsoft CDN': ['aspnetcdn.com', 'msecnd.net'],
        'Sucuri': ['sucuri.net'],
        'Imperva': ['incapsula']
      };
      
      for (const [cdn, patterns] of Object.entries(cdnPatterns)) {
        if (patterns.some(pattern => html.toLowerCase().includes(pattern))) {
          results.push({
            name: cdn,
            type: 'cdn',
            confidence: 'medium',
            source: 'deep-html-analysis'
          });
          break; // Only add the most likely CDN
        }
      }
      
      // Check for DNS provider mentions in HTML comments or scripts
      const dnsText = html.match(/DNS[^<>]{1,50}(?:provided by|powered by|using)[^<>]{1,50}([A-Za-z0-9 &.]+)/i);
      if (dnsText && dnsText[1]) {
        const provider = dnsText[1].trim();
        results.push({
          name: provider,
          type: 'dns-provider',
          confidence: 'medium',
          source: 'deep-html-analysis'
        });
      }
      
      // Get nameservers from meta tags
      const nameserverTag = doc.querySelector('meta[name="dns-server"], meta[name="nameserver"]');
      if (nameserverTag && nameserverTag.getAttribute('content')) {
        const nsContent = nameserverTag.getAttribute('content');
        if (nsContent.includes('godaddy')) {
          results.push({
            name: 'GoDaddy',
            type: 'domain-registrar',
            confidence: 'high',
            source: 'deep-html-meta'
          });
        } else if (nsContent.includes('cloudflare')) {
          results.push({
            name: 'Cloudflare',
            type: 'domain-registrar',
            confidence: 'high',
            source: 'deep-html-meta'
          });
        } else if (nsContent.includes('namecheap')) {
          results.push({
            name: 'Namecheap',
            type: 'domain-registrar',
            confidence: 'high',
            source: 'deep-html-meta'
          });
        }
      }
    } catch (error) {
      console.error('Error in deep HTML analysis:', error);
    }
  }
  
  /**
   * Fetch domain information from server
   * @private
   * @param {string} url - URL to analyze
   * @returns {Promise<Object>} - Domain information from server
   */
  async _fetchServerDomainInfo(url) {
    try {
      // Make sure we have the correct port in the API URL
      const apiPort = process.env.REACT_APP_API_PORT || '5000';
      const apiBaseUrl = this.apiBaseUrl.replace(/:\d+/, `:${apiPort}`);
      
      // Build the endpoint URL
      const endpoint = `${apiBaseUrl}/pagespeed/domain-info?url=${encodeURIComponent(url)}`;
      
      console.log('Fetching domain info from server:', endpoint);
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data && data.domainInfo) {
        console.log('Server returned domain info:', data.domainInfo);
        return data.domainInfo;
      }
      
      return null;
    } catch (error) {
      console.warn('Server domain info fetch failed:', error.message);
      return null;
    }
  }
  
  /**
   * Fetch domain information specifically about hosting and registrar
   * @private
   * @param {string} url - URL to analyze
   * @returns {Promise<Array>} - Technologies detected from domain info
   */
  async _fetchDomainInfo(url) {
    try {
      // Make sure we have the correct port in the API URL
      const apiPort = process.env.REACT_APP_API_PORT || '5000';
      const apiBaseUrl = this.apiBaseUrl.replace(/:\d+/, `:${apiPort}`);
      
      // Build the endpoint URL
      const endpoint = `${apiBaseUrl}/pagespeed/registry-info?url=${encodeURIComponent(url)}`;
      
      console.log('Fetching registry info from server:', endpoint);
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data && data.technologies && Array.isArray(data.technologies)) {
        console.log('Server registry info returned:', data);
        return data.technologies;
      }
      
      return [];
    } catch (error) {
      console.warn('Registry info fetch failed:', error.message);
      return [];
    }
  }
  
  /**
   * Fetch technology data from server
   * @private
   * @param {string} url - URL to analyze
   * @returns {Promise<Array>} - Technologies detected by server
   */
  async _fetchTechnologyData(url) {
    try {
      // Make sure we have the correct port in the API URL
      const apiPort = process.env.REACT_APP_API_PORT || '5000';
      const apiBaseUrl = this.apiBaseUrl.replace(/:\d+/, `:${apiPort}`);
      
      // Build the endpoint URL
      const endpoint = `${apiBaseUrl}/pagespeed/tech-detect?url=${encodeURIComponent(url)}`;
      
      console.log('Fetching technology data from server:', endpoint);
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`Server returned ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data && data.technologies && Array.isArray(data.technologies)) {
        // Map technology data to our format
        const mappedTechs = data.technologies.map(tech => {
          return {
            name: tech.name,
            type: tech.type || 'other',
            confidence: tech.confidence || 'medium',
            source: 'server-detection',
            version: tech.version || null
          };
        });
        
        return mappedTechs;
      }
      
      return [];
    } catch (error) {
      console.warn('Technology data fetch failed:', error.message);
      return [];
    }
  }
  
  /**
   * Use server-side technology detection as a comprehensive fallback
   * @private
   * @param {string} url - URL to analyze
   * @returns {Promise<Array>} - Technologies detected by server
   */
  async _useServerSideTechnologyDetection(url) {
    try {
      // Make sure we have the correct port in the API URL
      const apiPort = process.env.REACT_APP_API_PORT || '5002';
      const apiBaseUrl = this.apiBaseUrl.replace(/:\d+/, `:${apiPort}`);
      
      // First try the full tech detect endpoint
      console.log('[Technology Detection] Trying server full-tech-detect endpoint');
      try {
        // Build the full endpoint URL
        const fullEndpoint = `${apiBaseUrl}/pagespeed/full-tech-detect?url=${encodeURIComponent(url)}`;
        console.log('Fetching full server-side technology detection:', fullEndpoint);
        
        const fullResponse = await fetch(fullEndpoint, {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          },
          timeout: 10000 // 10 second timeout
        });
        
        if (fullResponse.ok) {
          const fullData = await fullResponse.json();
          
          if (fullData && fullData.technologies && Array.isArray(fullData.technologies)) {
            console.log(`[Technology Detection] Server full-tech-detect found ${fullData.technologies.length} technologies`);
            // Add source information to each tech
            const sourcedTechs = fullData.technologies.map(tech => ({
              ...tech,
              source: tech.source || 'server-full-tech-detect'
            }));
            
            return sourcedTechs;
          }
        } else {
          console.warn(`[Technology Detection] Server full-tech-detect failed with status ${fullResponse.status}`);
        }
      } catch (fullError) {
        console.warn('[Technology Detection] Error with full-tech-detect endpoint:', fullError.message);
      }
      
      // If full tech detect failed, try regular tech-detect endpoint
      console.log('[Technology Detection] Trying server tech-detect endpoint');
      try {
        // Build the endpoint URL
        const endpoint = `${apiBaseUrl}/pagespeed/tech-detect?url=${encodeURIComponent(url)}`;
        console.log('Fetching basic server-side technology detection:', endpoint);
        
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            'Accept': 'application/json'
          },
          timeout: 10000 // 10 second timeout
        });
        
        if (response.ok) {
          const data = await response.json();
          
          if (data && data.technologies && Array.isArray(data.technologies)) {
            console.log(`[Technology Detection] Server tech-detect found ${data.technologies.length} technologies`);
            // Add source information to each tech
            const sourcedTechs = data.technologies.map(tech => ({
              ...tech,
              source: tech.source || 'server-tech-detect'
            }));
            
            return sourcedTechs;
          }
        } else {
          console.warn(`[Technology Detection] Server tech-detect failed with status ${response.status}`);
        }
      } catch (error) {
        console.warn('[Technology Detection] Error with tech-detect endpoint:', error.message);
      }
      
      // If both endpoints failed, return empty array
      console.warn('[Technology Detection] All server detection methods failed');
      return [];
    } catch (error) {
      console.warn('[Technology Detection] Server-side detection failed:', error.message);
      return [];
    }
  }
  
  /**
   * Deduplicate and merge similar technology results
   * @private
   * @param {Array} technologies - Raw technology results
   * @returns {Array} - Deduplicated technologies
   */
  _deduplicateTechnologies(technologies) {
    if (!technologies || !Array.isArray(technologies)) {
      return [];
    }
    
    // Create a map to store unique technologies by name (case insensitive)
    const techMap = new Map();
    
    // Process each technology
    for (const tech of technologies) {
      // Skip invalid entries
      if (!tech || !tech.name) continue;
      
      // Normalize the name (lowercase for comparison)
      const normalizedName = tech.name.toLowerCase();
      
      // If we already have this tech, potentially merge/update it
      if (techMap.has(normalizedName)) {
        const existingTech = techMap.get(normalizedName);
        
        // Use the higher confidence if available
        if (tech.confidence && 
            (!existingTech.confidence || 
             this._getConfidenceRank(tech.confidence) > this._getConfidenceRank(existingTech.confidence))) {
          existingTech.confidence = tech.confidence;
        }
        
        // Keep version if available
        if (tech.version && !existingTech.version) {
          existingTech.version = tech.version;
        }
        
        // Add source information if different
        if (tech.source && tech.source !== existingTech.source) {
          existingTech.source = Array.isArray(existingTech.source) 
            ? [...existingTech.source, tech.source] 
            : [existingTech.source, tech.source];
        }
      } else {
        // First time seeing this technology, add it to the map
        // Clone the tech object and make source an array if it's not
        const techEntry = { ...tech };
        if (techEntry.source && !Array.isArray(techEntry.source)) {
          techEntry.source = [techEntry.source];
        } else if (!techEntry.source) {
          techEntry.source = ['client-side'];
        }
        
        techMap.set(normalizedName, techEntry);
      }
    }
    
    // Convert map values back to array
    return Array.from(techMap.values());
  }
  
  /**
   * Get numerical rank for confidence level
   * @private
   * @param {string} confidence - Confidence level string
   * @returns {number} - Numerical rank
   */
  _getConfidenceRank(confidence) {
    const ranks = {
      'low': 1,
      'medium': 2,
      'high': 3,
      'certain': 4
    };
    
    return ranks[confidence?.toLowerCase()] || 0;
  }
  
  /**
   * Group technologies by type for better presentation
   * @private
   * @param {Array} technologies - List of detected technologies
   * @returns {Object} - Technologies grouped by type
   */
  _groupTechnologiesByType(technologies) {
    const grouped = {};
    
    for (const tech of technologies) {
      const type = tech.type || 'other';
      
      if (!grouped[type]) {
        grouped[type] = [];
      }
      
      grouped[type].push(tech);
    }
    
    return grouped;
  }
  
  /**
   * Extract technology clues from PageSpeed analysis data
   * @private
   * @param {Object} analysisData - PageSpeed analysis data
   * @param {Array} results - Array to store detected technologies
   */
  _detectFromPageSpeedData(analysisData, results) {
    try {
      if (!analysisData || !analysisData.rawData) return;
      
      const rawData = analysisData.rawData;
      
      // Check for technology hints in the audit data
      if (rawData.audits) {
        // Check for server response time issues which could indicate server technology
        if (rawData.audits['server-response-time']) {
          const serverResponseTime = rawData.audits['server-response-time'].numericValue;
          if (serverResponseTime > 600) {
            // Slower server response might indicate a certain stack
            results.push({
              name: 'Traditional Server Stack',
              type: 'backend',
              confidence: 'low',
              source: 'pagespeed-analysis'
            });
          } else if (serverResponseTime < 200) {
            // Very fast response might indicate a static site or CDN
            results.push({
              name: 'Static Site or CDN',
              type: 'hosting',
              confidence: 'medium',
              source: 'pagespeed-analysis'
            });
          }
        }
        
        // Check for render-blocking resources which could indicate framework usage
        if (rawData.audits['render-blocking-resources'] && 
            rawData.audits['render-blocking-resources'].details &&
            rawData.audits['render-blocking-resources'].details.items) {
          
          const blockingResources = rawData.audits['render-blocking-resources'].details.items;
          
          // Check for common frameworks in resource URLs
          const frameworkPatterns = {
            'WordPress': /wp-(?:content|includes)/i,
            'jQuery': /jquery/i,
            'Bootstrap': /bootstrap/i,
            'React': /react/i,
            'Angular': /angular/i,
            'Vue.js': /vue/i
          };
          
          for (const resource of blockingResources) {
            if (resource.url) {
              for (const [framework, pattern] of Object.entries(frameworkPatterns)) {
                if (pattern.test(resource.url)) {
                  results.push({
                    name: framework,
                    type: framework === 'WordPress' ? 'cms' : 'frontend',
                    confidence: 'medium',
                    source: 'pagespeed-analysis'
                  });
                  break;
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error extracting from PageSpeed data:', error);
    }
  }
  
  /**
   * Fetch HTML content with multiple fallback methods to handle CORS errors
   * @private
   * @param {string} url - URL to fetch HTML from
   * @returns {Promise<string|null>} - HTML content or null if all methods fail
   */
  async _fetchHtmlWithFallbacks(url) {
    console.log(`[TechnologyDetectionService] Attempting to fetch HTML from ${url} with fallbacks`);
    
    // Method 1: Use our server-side proxy API (most reliable)
    try {
      console.log(`[TechnologyDetectionService] Trying server proxy API`);
      const encodedUrl = encodeURIComponent(url);
      const endpoint = `${this.apiBaseUrl}/pagespeed/fetch-html?url=${encodedUrl}`;
      
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data && data.html) {
        console.log(`[TechnologyDetectionService] Server proxy fetch successful (${data.html.length} bytes)`);
        return data.html;
      } else {
        throw new Error('No HTML content in response');
      }
    } catch (serverError) {
      console.warn('[TechnologyDetectionService] Server proxy error:', serverError.message);
    }
    
    // Method 2: Direct fetch - will often fail due to CORS
    try {
      console.log(`[TechnologyDetectionService] Trying direct fetch`);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'text/html',
          'User-Agent': 'Mozilla/5.0 (compatible; TechnologyDetector/1.0)'
        },
        credentials: 'omit'
      });
      
      if (response.ok) {
        const html = await response.text();
        console.log(`[TechnologyDetectionService] Direct fetch successful (${html.length} bytes)`);
        return html;
      } else {
        throw new Error(`Direct fetch failed with status: ${response.status}`);
      }
    } catch (directError) {
      console.warn('[TechnologyDetectionService] Direct fetch error:', directError.message);
    }
    
    // Method 3: Try CORS proxies
    for (const corsProxy of this.corsProxies) {
      try {
        console.log(`[TechnologyDetectionService] Trying CORS proxy: ${corsProxy.split('?')[0]}`);
        const proxyUrl = `${corsProxy}${encodeURIComponent(url)}`;
        
        const response = await fetch(proxyUrl, {
          method: 'GET',
          headers: {
            'Accept': 'text/html'
          }
        });
        
        if (response.ok) {
          const html = await response.text();
          if (html && html.length > 100) { // Make sure we got meaningful content
            console.log(`[TechnologyDetectionService] CORS proxy fetch successful (${html.length} bytes)`);
            return html;
          }
        }
      } catch (proxyError) {
        console.warn(`[TechnologyDetectionService] CORS proxy error:`, proxyError.message);
      }
    }
    
    // All methods failed
    console.error('[TechnologyDetectionService] All HTML fetch methods failed');
    return null;
  }
  
  /**
   * Detect technologies based on URL patterns
   * @private
   * @param {string} url - URL to analyze
   * @param {Array} results - Array to store detected technologies
   */
  _detectFromUrl(url, results) {
    if (!url) return;
    
    try {
      const urlObj = new URL(url);
      const hostname = urlObj.hostname.toLowerCase();
      const path = urlObj.pathname.toLowerCase();
      
      console.log(`Analyzing URL patterns for: ${hostname}${path}`);
      
      // Basic protocol detection (every site has this)
      results.push({
        name: urlObj.protocol === 'https:' ? 'HTTPS' : 'HTTP',
        type: 'protocol',
        confidence: 'high',
        source: 'url-analysis'
      });
      
      // CMS Detection
      if (hostname.includes('wordpress.com') || path.includes('/wp-') || path.includes('/wp-content/') || path.includes('/wp-includes/')) {
        results.push({
          name: 'WordPress',
          type: 'cms',
          confidence: 'high',
          source: 'url-analysis'
        });
        
        // WordPress typically uses PHP and MySQL
        results.push({
          name: 'PHP',
          type: 'backend',
          confidence: 'high',
          source: 'url-analysis'
        });
        
        results.push({
          name: 'MySQL',
          type: 'database',
          confidence: 'medium',
          source: 'url-analysis'
        });
      }
      
      // Common Ecommerce Platforms
      if (hostname.includes('shopify.com') || hostname.includes('myshopify.com')) {
        results.push({
          name: 'Shopify',
          type: 'ecommerce',
          confidence: 'high',
          source: 'url-analysis'
        });
      } else if (path.includes('/woocommerce/') || path.includes('/wp-content/plugins/woocommerce/')) {
        results.push({
          name: 'WooCommerce',
          type: 'ecommerce',
          confidence: 'high',
          source: 'url-analysis'
        });
      } else if (path.includes('/magento/') || hostname.includes('magento')) {
        results.push({
          name: 'Magento',
          type: 'ecommerce',
          confidence: 'high',
          source: 'url-analysis'
        });
      }
      
      // Website Builders
      if (hostname.includes('wix.com') || hostname.includes('wixsite.com')) {
        results.push({
          name: 'Wix',
          type: 'cms',
          confidence: 'high',
          source: 'url-analysis'
        });
      } else if (hostname.includes('squarespace.com')) {
        results.push({
          name: 'Squarespace',
          type: 'cms',
          confidence: 'high',
          source: 'url-analysis'
        });
      } else if (hostname.includes('webflow.io') || hostname.includes('webflow.com')) {
        results.push({
          name: 'Webflow',
          type: 'cms',
          confidence: 'high',
          source: 'url-analysis'
        });
      }
      
      // Hosting Platforms
      if (hostname.includes('github.io')) {
        results.push({
          name: 'GitHub Pages',
          type: 'hosting',
          confidence: 'high',
          source: 'url-analysis'
        });
      } else if (hostname.includes('netlify.app') || hostname.includes('netlify.com')) {
        results.push({
          name: 'Netlify',
          type: 'hosting',
          confidence: 'high',
          source: 'url-analysis'
        });
      } else if (hostname.includes('vercel.app') || hostname.includes('now.sh')) {
        results.push({
          name: 'Vercel',
          type: 'hosting',
          confidence: 'high',
          source: 'url-analysis'
        });
      } else if (hostname.includes('herokuapp.com')) {
        results.push({
          name: 'Heroku',
          type: 'hosting',
          confidence: 'high',
          source: 'url-analysis'
        });
      } else if (hostname.includes('wpengine.com')) {
        results.push({
          name: 'WP Engine',
          type: 'hosting',
          confidence: 'high',
          source: 'url-analysis'
        });
      } else if (hostname.includes('cloudfront.net')) {
        results.push({
          name: 'AWS CloudFront',
          type: 'cdn',
          confidence: 'high',
          source: 'url-analysis'
        });
        
        results.push({
          name: 'Amazon AWS',
          type: 'hosting',
          confidence: 'medium',
          source: 'url-analysis'
        });
      } else if (hostname.includes('azurewebsites.net')) {
        results.push({
          name: 'Microsoft Azure',
          type: 'hosting',
          confidence: 'high',
          source: 'url-analysis'
        });
      }
      
      // Check for common file extensions indicating tech stack
      if (path.endsWith('.php')) {
        results.push({
          name: 'PHP',
          type: 'backend',
          confidence: 'high',
          source: 'url-analysis'
        });
      } else if (path.endsWith('.aspx') || path.endsWith('.asp')) {
        results.push({
          name: 'ASP.NET',
          type: 'backend',
          confidence: 'high',
          source: 'url-analysis'
        });
      } else if (path.endsWith('.jsp')) {
        results.push({
          name: 'Java',
          type: 'backend',
          confidence: 'high',
          source: 'url-analysis'
        });
      } else if (path.endsWith('.rb') || path.includes('/rails/')) {
        results.push({
          name: 'Ruby on Rails',
          type: 'backend',
          confidence: 'high',
          source: 'url-analysis'
        });
      }
      
      // Check for web frameworks in URL
      if (path.includes('/wp-content/themes/')) {
        // Extract WordPress theme if possible
        const themeMatch = path.match(/\/wp-content\/themes\/([^/]+)/);
        if (themeMatch && themeMatch[1]) {
          results.push({
            name: `WordPress Theme: ${themeMatch[1].replace(/-/g, ' ')}`,
            type: 'wordpress-theme',
            confidence: 'high',
            source: 'url-analysis'
          });
        }
      } else if (path.includes('/wp-content/plugins/')) {
        // Extract WordPress plugin if possible
        const pluginMatch = path.match(/\/wp-content\/plugins\/([^/]+)/);
        if (pluginMatch && pluginMatch[1]) {
          results.push({
            name: `WordPress Plugin: ${pluginMatch[1].replace(/-/g, ' ')}`,
            type: 'wordpress-plugin',
            confidence: 'high',
            source: 'url-analysis'
          });
        }
      }
      
      // Check for JavaScript frameworks
      if (path.includes('/angular/') || path.includes('/ng/')) {
        results.push({
          name: 'Angular',
          type: 'frontend',
          confidence: 'medium',
          source: 'url-analysis'
        });
      } else if (path.includes('/react/') || path.includes('react-')) {
        results.push({
          name: 'React',
          type: 'frontend',
          confidence: 'medium',
          source: 'url-analysis'
        });
      } else if (path.includes('/vue/') || path.includes('vue-')) {
        results.push({
          name: 'Vue.js',
          type: 'frontend',
          confidence: 'medium',
          source: 'url-analysis'
        });
      }
      
      // Check for miscellaneous frameworks and libraries
      if (path.includes('/bootstrap/') || path.includes('bootstrap.')) {
        results.push({
          name: 'Bootstrap',
          type: 'frontend',
          confidence: 'medium',
          source: 'url-analysis'
        });
      } else if (path.includes('/jquery/') || path.includes('jquery.')) {
        results.push({
          name: 'jQuery',
          type: 'frontend',
          confidence: 'medium',
          source: 'url-analysis'
        });
      }
      
    } catch (error) {
      console.error('Error in URL pattern analysis:', error);
    }
  }
  
  /**
   * Add default technologies if detection fails
   * @private
   * @param {Array} results - Array to store detected technologies
   */
  _addDefaultTechnologies(results) {
    // Always add these basic web technologies
    results.push({
      name: 'HTML',
      type: 'frontend',
      confidence: 'high',
      source: 'default'
    });
    
    results.push({
      name: 'CSS',
      type: 'frontend',
      confidence: 'high',
      source: 'default'
    });
    
    results.push({
      name: 'JavaScript',
      type: 'frontend',
      confidence: 'medium',
      source: 'default'
    });
    
    // Add placeholder backend and hosting with low confidence
    results.push({
      name: 'Unknown Backend',
      type: 'backend',
      confidence: 'low',
      source: 'default'
    });
    
    results.push({
      name: 'Traditional Hosting',
      type: 'hosting',
      confidence: 'low',
      source: 'default'
    });
  }
  
  /**
   * Map technology category to our type
   * @private
   * @param {string} category - Technology category
   * @returns {string} - Mapped type
   */
  _mapTechnologyCategory(category) {
    if (!category) {
      return 'other';
    }
    
    // Normalize the category string
    const normalized = category.toLowerCase();
    
    // Map common categories to our types
    const categoryMap = {
      'cms': 'cms',
      'ecommerce': 'ecommerce',
      'web-server': 'web-server',
      'backend': 'backend',
      'frontend': 'frontend',
      'framework': 'framework',
      'database': 'database',
      'hosting': 'hosting',
      'analytics': 'analytics',
      'cdn': 'cdn',
      'security': 'security',
      'payment': 'payment',
      'marketing': 'marketing',
      'ui-framework': 'frontend',
      'javascript-library': 'frontend',
      'programming-language': 'backend',
      'operating-system': 'server'
    };
    
    return categoryMap[normalized] || 'other';
  }
}

// Export a singleton instance
const technologyDetectionService = new TechnologyDetectionService();
export default technologyDetectionService; 